import {
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  useTheme,
} from '@mui/material';
import { Column, useTable } from 'react-table';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { getDateColumns } from './hooks/get-date-columns';
import { CalendarGridProps } from './calendar-grid.types';

export const CalendarGrid = <TableColumns extends object, DateData extends object[]>(
  gridOpts: CalendarGridProps<TableColumns, DateData>
) => {
  const theme = useTheme();
  const { mode, startDate, cellFn, headerFn, onScroll } = gridOpts;

  const dateColumns = useMemo(
    () => getDateColumns(mode, startDate, cellFn, headerFn),
    [mode, startDate, cellFn, headerFn]
  );
  const isCurrentDate = useCallback(
    (column: Column<TableColumns>) =>
      startDate.month === DateTime.now().month &&
      startDate.year === DateTime.now().year &&
      column.id == `DAY_${DateTime.now().day}`,
    [startDate]
  );
  const allColumns = useMemo(
    () => [...gridOpts.columns, ...dateColumns] as Column<TableColumns>[],
    [gridOpts.columns, dateColumns]
  );

  const { getTableProps, headerGroups, rows, prepareRow, columns } = useTable({
    ...gridOpts,
    columns: allColumns,
  });

  return (
    <StyledTableWrapper onScroll={onScroll}>
      <StyledTable stickyHeader={true} {...getTableProps()}>
        <colgroup>
          {columns.map((column) => {
            return (
              <StyledCurrentDateColumn
                key={column.id.toString()}
                width={column.width}
                isCurrentDate={isCurrentDate && isCurrentDate(column as Column<TableColumns>)}
                theme={theme}
              />
            );
          })}
        </colgroup>
        <TableHead {...getTableProps()}>
          {headerGroups.map((headerGroup) => (
            <StyledHeaderRow
              {...headerGroup.getHeaderGroupProps()}
              key={headerGroup.headers.map((i) => i.id).join('__')}
            >
              {headerGroup.headers.map((column) => (
                <StyledHeaderCell
                  {...column.getHeaderProps()}
                  key={column.id}
                  theme={theme}
                  isCurrentDate={isCurrentDate && isCurrentDate(column as Column<TableColumns>)}
                >
                  {column.render('Header')}
                </StyledHeaderCell>
              ))}
            </StyledHeaderRow>
          ))}
        </TableHead>
        <TableBody {...getTableProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <StyledTableRow {...row.getRowProps()} key={row.id}>
                {row.cells.map((cell) => (
                  <StyledDataCell {...cell.getCellProps()} key={`${row.id}__${cell.column.id}`}>
                    {cell.render('Cell')}
                  </StyledDataCell>
                ))}
              </StyledTableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </StyledTableWrapper>
  );
};

const StyledTable = styled(Table)(({ theme }) => ({
  '& tbody tr:has(input[type="checkbox"]:checked)': {
    backgroundColor: theme.palette.lwPrimary?.[5] ?? 'white',
  },
  '& tbody tr:is(.row-group-before, .row-group-last, .row-group-single) td': {
    borderBottom: 'none',
    paddingBottom: '17px',
  },
  // styling of the 'tr' children is necessary, because border radius cannot be applied to a 'tr'
  '& tbody tr.row-group-first td:first-of-type': { borderTopLeftRadius: '16px' },
  '& tbody tr.row-group-first td:last-child': { borderTopRightRadius: '16px' },
  '& tbody tr.row-group-last td:first-of-type': { borderBottomLeftRadius: '16px' },
  '& tbody tr.row-group-last td:last-child': { borderBottomRightRadius: '16px' },
  '& tbody tr.row-group-single td:first-of-type': { borderRadius: '16px 0 0 16px' },
  '& tbody tr.row-group-single td:last-child': { borderRadius: '0 16px 16px 0' },
  '& tbody': {
    borderTop: `solid 2px ${theme.palette.lwSecondary?.[20]}`,
    borderBottom: `solid 2px ${theme.palette.lwSecondary?.[20]}`,
  },
  '& button.MuiButtonBase-root': {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  tableLayout: 'fixed',
}));

const StyledHeaderCell = styled(TableCell)(
  ({ theme, isCurrentDate }: { theme: Theme; isCurrentDate: boolean }) => ({
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.lwSecondary?.[10] ?? 'lightgray',
    backgroundColor: isCurrentDate ? theme.palette.lwPrimary?.[5] : undefined,
    textAlign: 'center',
  })
);

const StyledCurrentDateColumn = styled('col')(
  ({
    theme,
    width,
    isCurrentDate,
  }: {
    theme: Theme;
    width: string | number | undefined;
    isCurrentDate: boolean;
  }) => ({
    backgroundColor: isCurrentDate ? theme.palette.lwPrimary?.[5] : 'transparent',
    width,
  })
);

const StyledHeaderRow = styled(TableRow)({
  height: '30px',
});

const StyledTableRow = styled(TableRow)({
  height: '50px',
});

const StyledDataCell = styled(TableCell)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  backgroundColor: undefined,
  padding: 0,
  border: '1px solid',
  borderColor: theme.palette.lwSecondary?.[10] ?? 'lightgray',
  height: '100%',
  verticalAlign: 'top',
}));

const StyledTableWrapper = styled(TableContainer)(() => ({
  height: '100%',
  overflow: 'auto',
  outline: 'none',
}));
